import {
  fold as foldSpelled,
  matchTerm,
  type Match,
  type TextQueryTerm,
  type TextRange,
} from '../lib/textQuery';

/** One run of text, marked when it matches the query. */
export interface NxHighlightSegment {
  text: string;
  hit: boolean;
}

/**
 * What a caller searches for: a string (its words, or as a whole phrase), a
 * list of strings (one term each), or the terms of a text query
 * (`lib/textQuery`), each with its own match mode and case flag.
 */
export type NxHighlightQuery = string | readonly string[] | readonly TextQueryTerm[];

export interface NxHighlightOptions {
  /**
   * For a string query: `'words'` marks every word of the query on its own,
   * `'phrase'` the query as a whole. Default `'words'`. A list of strings is
   * always one term each, and a `TextQueryTerm` brings its own `match`.
   */
  match?: 'words' | 'phrase';
  /** Terms shorter than this (after folding) are left unmarked. Default 1. */
  minLength?: number;
  /**
   * For a string query or a list of strings. A `TextQueryTerm` carries its own
   * `caseSensitive`; this option is ignored for it. Default `false`.
   */
  caseSensitive?: boolean;
  /**
   * „Mull“ also marks „Müll“. Default `true`. Belongs to the `'accents'` fold;
   * the `'spelled'` fold always folds accents.
   */
  ignoreDiacritics?: boolean;
  /**
   * At most this many terms count (the rest are dropped; of a list of
   * `TextQueryTerm`s only those that can mark count). Default 10.
   */
  maxTerms?: number;
  /**
   * How text and terms are folded before they are compared.
   *
   * - `'accents'` (default, what every caller has always had): case falls away
   *   and, with `ignoreDiacritics`, so do accents — „Mull“ marks „Müll“. Every
   *   start of a term counts, so two hits of one term may overlap („aa“ in
   *   „aaa“ marks all three letters). A hit of a `TextQueryTerm` takes the
   *   combining marks behind it along (`Cafe` + U+0301 is marked whole for
   *   `cafe`), as the search does; a string or a list of strings keeps ending in
   *   front of them, as it always did.
   * - `'spelled'`: the fold of the search itself (`lib/textQuery`) — `ü` is `ue`,
   *   `ß` is `ss`, so „Müller“ is marked for `mueller` and „Straße“ for
   *   `strasse` — and the ranges are exactly the ones the search reports
   *   (`matchTerm`: the hits of one term never overlap), on the letters of the
   *   original text, a letter and its combining marks as one. `ignoreDiacritics`
   *   has no effect there.
   */
  fold?: 'accents' | 'spelled';
}

interface Folded {
  text: string;
  /** For each folded code unit: start and end of the original character. */
  from: number[];
  to: number[];
}

function foldChar(char: string, caseSensitive: boolean, ignoreDiacritics: boolean): string {
  let out = ignoreDiacritics ? char.normalize('NFD').replace(/\p{M}/gu, '') : char;
  if (!caseSensitive) out = out.toLowerCase();
  return out;
}

function fold(text: string, caseSensitive: boolean, ignoreDiacritics: boolean): Folded {
  const folded: Folded = { text: '', from: [], to: [] };
  let at = 0;
  for (const char of text) {
    const out = foldChar(char, caseSensitive, ignoreDiacritics);
    for (let i = 0; i < out.length; i++) {
      folded.from.push(at);
      folded.to.push(at + char.length);
    }
    folded.text += out;
    at += char.length;
  }
  return folded;
}

function foldTerm(term: string, caseSensitive: boolean, ignoreDiacritics: boolean): string {
  let out = '';
  for (const char of term) out += foldChar(char, caseSensitive, ignoreDiacritics);
  return out;
}

// ── White space and words ────────────────────────────────────────────────

const WHITE_SPACE = /\p{White_Space}/u;

/**
 * Unicode `White_Space` of one UTF-16 unit (all of it is in the BMP), the way
 * the search counts it. Not `\s`: that one has U+FEFF and misses U+0085.
 */
function isSpace(unit: string): boolean {
  const code = unit.charCodeAt(0);
  if ((code > 0x20 && code < 0x85) || code > 0x3000) return false;
  return WHITE_SPACE.test(unit);
}

function trimSpace(text: string): string {
  let start = 0;
  let end = text.length;
  while (start < end && isSpace(text[start]!)) start += 1;
  while (end > start && isSpace(text[end - 1]!)) end -= 1;
  return text.slice(start, end);
}

/** Every run of white space as one space — the form a phrase is compared in. */
function collapseSpace(text: string): string {
  let out = '';
  let inRun = false;
  for (const unit of text) {
    if (isSpace(unit)) {
      if (!inRun) out += ' ';
      inRun = true;
    } else {
      out += unit;
      inRun = false;
    }
  }
  return out;
}

/** A folded text with every white-space run as one space that maps to the whole run. */
function collapseFolded(folded: Folded): Folded {
  const out: Folded = { text: '', from: [], to: [] };
  let inRun = false;
  for (let i = 0; i < folded.text.length; i += 1) {
    if (isSpace(folded.text[i]!)) {
      if (inRun) {
        out.to[out.to.length - 1] = folded.to[i]!;
        continue;
      }
      out.text += ' ';
      inRun = true;
    } else {
      out.text += folded.text[i];
      inRun = false;
    }
    out.from.push(folded.from[i]!);
    out.to.push(folded.to[i]!);
  }
  return out;
}

/** The words of a folded text: maximal runs of characters that are not white space. */
function wordsOf(text: string): TextRange[] {
  const out: TextRange[] = [];
  let start = -1;
  for (let i = 0; i < text.length; i += 1) {
    if (isSpace(text[i]!)) {
      if (start >= 0) out.push([start, i]);
      start = -1;
    } else if (start < 0) {
      start = i;
    }
  }
  if (start >= 0) out.push([start, text.length]);
  return out;
}

// ── Terms ────────────────────────────────────────────────────────────────

/** A term to look for, as asked: before it is folded. */
interface Candidate {
  /** The text, trimmed. */
  source: string;
  match: Match;
  caseSensitive: boolean;
}

/** A candidate that is used: `folded` is what it is compared as. */
interface Needle extends Candidate {
  folded: string;
}

const MATCHES: ReadonlySet<unknown> = new Set<Match>([
  'contains',
  'startsWith',
  'endsWith',
  'phrase',
]);

/** A list of terms holds objects; a list of strings holds none. */
function isTermList(query: readonly unknown[]): query is readonly TextQueryTerm[] {
  return query.some((item) => typeof item === 'object' && item !== null);
}

/**
 * The terms a query asks for, in order. Of a list of `TextQueryTerm`s only
 * those that mark are left: `must` and `should`, not disabled, with a known
 * match mode — the ones the search takes its hits from.
 */
function candidatesOf(query: NxHighlightQuery, options: NxHighlightOptions): Candidate[] {
  const caseSensitive = options.caseSensitive ?? false;
  if (typeof query !== 'string') {
    if (!isTermList(query)) {
      return query.map((item) => ({ source: item.trim(), match: 'contains', caseSensitive }));
    }
    const out: Candidate[] = [];
    for (const item of query) {
      if (!item || typeof item.text !== 'string' || item.disabled) continue;
      if (item.occur !== 'must' && item.occur !== 'should') continue;
      if (!MATCHES.has(item.match)) continue;
      out.push({
        source: trimSpace(item.text),
        match: item.match,
        caseSensitive: item.caseSensitive === true,
      });
    }
    return out;
  }
  if (options.fold === 'spelled') {
    // The words of a string are `contains` terms, the phrase a `phrase` term — as the search reads them.
    return options.match === 'phrase'
      ? [{ source: query.trim(), match: 'phrase', caseSensitive }]
      : query
          .trim()
          .split(/\p{White_Space}+/u)
          .map((source) => ({ source, match: 'contains', caseSensitive }));
  }
  const raw = options.match === 'phrase' ? [query.trim()] : query.trim().split(/\s+/);
  return raw.map((source) => ({ source, match: 'contains', caseSensitive }));
}

/**
 * The terms that are used: folded, long enough, each only once (the same text
 * with the same match mode and case flag), and at most `maxTerms` of them.
 */
function needlesOf(candidates: readonly Candidate[], options: NxHighlightOptions): Needle[] {
  const spelled = options.fold === 'spelled';
  const ignoreDiacritics = options.ignoreDiacritics ?? true;
  const minLength = Math.max(1, options.minLength ?? 1);
  const maxTerms = options.maxTerms ?? 10;
  const needles: Needle[] = [];
  const seen = new Set<string>();
  for (const candidate of candidates) {
    let folded = spelled
      ? foldSpelled(candidate.source, { caseSensitive: candidate.caseSensitive }).text
      : foldTerm(candidate.source, candidate.caseSensitive, ignoreDiacritics);
    if (candidate.match === 'phrase') folded = collapseSpace(folded);
    const key = `${candidate.match}:${candidate.caseSensitive ? 'case' : 'any'}:${folded}`;
    if ([...folded].length < minLength || seen.has(key)) continue;
    seen.add(key);
    needles.push({ ...candidate, folded });
    if (needles.length >= maxTerms) break;
  }
  return needles;
}

// ── Hits ─────────────────────────────────────────────────────────────────

/** A text folded one way, with what the match modes derive from it, each only when asked for. */
interface Hay {
  folded: Folded;
  collapsed?: Folded;
  words?: TextRange[];
}

/** Every occurrence of `needle`, each start counted — a hit may overlap the one before. */
function occurrences(text: string, needle: string): TextRange[] {
  const out: TextRange[] = [];
  for (let at = text.indexOf(needle); at >= 0; at = text.indexOf(needle, at + 1)) {
    out.push([at, at + needle.length]);
  }
  return out;
}

/** The words that start (or end) with `needle`: one hit per word, never across one. */
function atWordEdge(
  text: string,
  words: readonly TextRange[],
  needle: string,
  edge: 'start' | 'end'
): TextRange[] {
  const out: TextRange[] = [];
  for (const [start, end] of words) {
    if (end - start < needle.length) continue;
    const at = edge === 'start' ? start : end - needle.length;
    if (text.startsWith(needle, at)) out.push([at, at + needle.length]);
  }
  return out;
}

/** The hits of one term under the accent fold, as ranges of the original text. */
function accentHits(hay: Hay, needle: Needle): TextRange[] {
  const folded =
    needle.match === 'phrase' ? (hay.collapsed ??= collapseFolded(hay.folded)) : hay.folded;
  let spans: TextRange[];
  switch (needle.match) {
    case 'contains':
    case 'phrase':
      spans = occurrences(folded.text, needle.folded);
      break;
    case 'startsWith':
    case 'endsWith': {
      const words = (hay.words ??= wordsOf(folded.text));
      const edge = needle.match === 'startsWith' ? 'start' : 'end';
      spans = atWordEdge(folded.text, words, needle.folded, edge);
      break;
    }
    default:
      return [];
  }
  return spans.map(([start, end]) => [folded.from[start]!, folded.to[end - 1]!]);
}

// ── Clusters ─────────────────────────────────────────────────────────────

/** A nonspacing mark: what `lib/textQuery` binds to the code point in front of it. */
const NONSPACING_MARK = /^\p{Mn}$/u;

/** Whether the code point that starts at `at` is a nonspacing mark (none sits below U+0300). */
function markAt(text: string, at: number): boolean {
  const code = text.codePointAt(at)!;
  return code >= 0x300 && NONSPACING_MARK.test(String.fromCodePoint(code));
}

/** Where the code point in front of `at` starts: a surrogate pair is one. */
function pointBefore(text: string, at: number): number {
  const low = text.charCodeAt(at - 1);
  const high = at >= 2 ? text.charCodeAt(at - 2) : 0;
  return low >= 0xdc00 && low <= 0xdfff && high >= 0xd800 && high <= 0xdbff ? at - 2 : at - 1;
}

/**
 * The range with the letters it cuts taken whole. A range that ends right
 * before nonspacing marks (`\p{Mn}`) takes them along, and one that starts on a
 * mark starts at the letter the mark belongs to: a hit never splits a letter
 * from its accent, as the ranges of the search never do.
 */
function wholeClusters(text: string, [start, end]: TextRange): TextRange {
  let from = start;
  while (from > 0 && markAt(text, from)) from = pointBefore(text, from);
  let to = end;
  while (to < text.length && markAt(text, to)) to += text.codePointAt(to)! > 0xffff ? 2 : 1;
  return [from, to];
}

/**
 * The hits of every term under the accent fold. `byCluster` (a list of terms)
 * widens each hit to whole clusters; a string or a list of strings keeps the
 * ranges it has always had, which end in front of a mark that follows.
 */
function accentRanges(
  text: string,
  needles: readonly Needle[],
  ignoreDiacritics: boolean,
  byCluster: boolean
): TextRange[] {
  const hays = new Map<boolean, Hay>();
  const ranges: TextRange[] = [];
  for (const needle of needles) {
    let hay = hays.get(needle.caseSensitive);
    if (!hay) {
      hay = { folded: fold(text, needle.caseSensitive, ignoreDiacritics) };
      hays.set(needle.caseSensitive, hay);
    }
    for (const range of accentHits(hay, needle)) {
      ranges.push(byCluster ? wholeClusters(text, range) : range);
    }
  }
  return ranges;
}

/** The hits under the search's own fold: `matchTerm`, term by term. */
function spelledRanges(text: string, needles: readonly Needle[]): TextRange[] {
  return needles.flatMap((needle) =>
    matchTerm(text, {
      text: needle.source,
      match: needle.match,
      caseSensitive: needle.caseSensitive,
    })
  );
}

/**
 * The ranges that can mark `text`: finite integer indexes, inside the text,
 * and not empty (`start < end`). Anything else is dropped, not clipped. The
 * caller's array is left as it is.
 */
function usableRanges(text: string, ranges: readonly (readonly [number, number])[]): TextRange[] {
  const limit = text.length;
  const out: TextRange[] = [];
  for (const range of ranges) {
    if (!range) continue;
    const start = range[0];
    const end = range[1];
    if (typeof start !== 'number' || typeof end !== 'number') continue;
    if (!Number.isInteger(start) || !Number.isInteger(end)) continue;
    if (start < 0 || end > limit || start >= end) continue;
    out.push([start, end]);
  }
  return out;
}

/**
 * The `<mark>` runs around `ranges`. Overlapping and touching ranges merge;
 * the ranges are sorted first. An empty text yields no runs, no range the
 * text unmarked. `ranges` is not mutated.
 */
function segmentsFromRanges(text: string, ranges: readonly TextRange[]): NxHighlightSegment[] {
  if (!text) return [];
  if (!ranges.length) return [{ text, hit: false }];
  const ordered = [...ranges].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged: TextRange[] = [];
  for (const range of ordered) {
    const last = merged[merged.length - 1];
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1]);
    else merged.push([range[0], range[1]]);
  }

  const segments: NxHighlightSegment[] = [];
  let at = 0;
  for (const [start, end] of merged) {
    if (start > at) segments.push({ text: text.slice(at, start), hit: false });
    segments.push({ text: text.slice(start, end), hit: true });
    at = end;
  }
  if (at < text.length) segments.push({ text: text.slice(at), hit: false });
  return segments;
}

/**
 * Splits `text` into runs around the matches of `query`, for `<mark>`
 * rendering without `v-html`. Overlapping and touching hits merge into one
 * run; matching ignores case and accents unless told otherwise. A blank
 * query yields the text unmarked; an empty text yields no runs.
 *
 * The query is a string (its words, or `match: 'phrase'` as a whole), a list
 * of strings (one term each) or a list of `TextQueryTerm`s of the search.
 * Terms mark as the search matches them: `mustNot` and disabled terms never
 * mark, `contains` finds the term anywhere, `startsWith` / `endsWith` only at
 * the start / end of a word (a run of characters that are not white space) and
 * `phrase` with every run of white space counted as one space. Each term uses
 * its own case flag — the `caseSensitive` option is ignored for terms.
 * `maxTerms` counts the terms that can mark, `minLength` their folded length.
 * `fold: 'spelled'` takes the ranges from the search's own matcher; under the
 * default fold a term's hit never splits a letter from its combining marks.
 * Ranges a caller already has go through {@link splitHighlightRanges}; this
 * function does not read them.
 */
export function splitHighlight(
  text: string,
  query: NxHighlightQuery,
  options: NxHighlightOptions = {}
): NxHighlightSegment[] {
  if (!text) return [];
  const needles = needlesOf(candidatesOf(query, options), options);
  if (!needles.length) return [{ text, hit: false }];

  const ranges =
    options.fold === 'spelled'
      ? spelledRanges(text, needles)
      : accentRanges(
          text,
          needles,
          options.ignoreDiacritics ?? true,
          typeof query !== 'string' && isTermList(query)
        );
  return segmentsFromRanges(text, ranges);
}

/**
 * Splits `text` into the same `<mark>` runs as `splitHighlight`, around the
 * given half-open UTF-16 ranges `[start, end)` instead of a query. A range
 * that is not a finite integer, that lies outside the text, or that is empty
 * (`start >= end`) is dropped. The rest are sorted; overlapping and touching
 * ranges merge. An empty text yields no runs; `null`, `undefined` or no
 * usable range yields the text unmarked. This function has no query: the
 * component is what ignores `query` only once `ranges` is an array.
 */
export function splitHighlightRanges(
  text: string,
  ranges: readonly (readonly [number, number])[] | null | undefined
): NxHighlightSegment[] {
  if (!text) return [];
  return segmentsFromRanges(text, usableRanges(text, ranges ?? []));
}

/**
 * The excerpt of an `NxHighlight` (`excerpt`, I-2871): the segments start
 * `context` characters before the first hit, moved forward to the next word
 * so no word is cut, behind an unmarked „… “. Everything after stays, so
 * later hits stay marked; the caller's line clamp cuts the end. Without a
 * hit, or with the first hit inside the context, the segments come back as
 * they are.
 */
export function excerptSegments(
  segments: readonly NxHighlightSegment[],
  context = 24
): NxHighlightSegment[] {
  const first = segments.findIndex((segment) => segment.hit);
  if (first < 0) return [...segments];
  const start = segments.slice(0, first).reduce((n, segment) => n + segment.text.length, 0);
  let cut = Math.max(0, start - Math.max(0, context));
  if (cut === 0) return [...segments];
  const text = segments.map((segment) => segment.text).join('');
  if (!/\s/.test(text[cut - 1]!)) {
    const space = text.slice(cut, start).search(/\s/);
    if (space >= 0) cut += space;
  }
  while (cut < start && /\s/.test(text[cut]!)) cut += 1;
  // Never start inside a surrogate pair.
  const code = text.charCodeAt(cut);
  if (code >= 0xdc00 && code <= 0xdfff) cut += 1;
  const out: NxHighlightSegment[] = [{ text: '… ', hit: false }];
  let at = 0;
  for (const segment of segments) {
    const end = at + segment.text.length;
    if (end > cut) out.push({ text: segment.text.slice(Math.max(0, cut - at)), hit: segment.hit });
    at = end;
  }
  return out;
}
